// frontend/src/pages/quotes/QuoteCreate.js
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createQuote } from '../../store/slices/quoteSlice';
import api from '../../services/api';
import useScrollSpy from '../../hooks/useScrollSpy';
import usePersistedState from '../../hooks/usePersistedState';
import Select from 'react-select';
import {
    Ship, Shield, DollarSign, Calendar, User, FileText, Upload,
    Percent, MapPin, AlertCircle, X, CheckCircle, Loader2,
    ChevronUp, ChevronDown, Plus, Search, TrendingUp, UserPlus,
    Landmark, Trash2, Eye, Download, Sparkles, Lock, Info, ArrowRight,
} from 'lucide-react';

// =========================================================
// CONSTANTS
// =========================================================
const INITIAL_FORM = {
    vessel_id: '',
    product_id: '',
    sum_insured: '',
    currency: 'USD',
    mode_of_transport: 'Sea',
    marine_rate: '0.20',
    war_rate: '0.05',
    custom_bank_interest: '',
    coverage_start: '',
    coverage_end: '',
    client_id: '',
    invoice_number: '',
    bill_of_lading: '',
    from_location: '',
    to_location: '',
    insured_name: '',
    interests_insured: '',
    comments_conditions: '',
    other_terms: '',
    open_cover_id: '',
};

const INITIAL_FILES = {
    kra_pin: null,
    certificate_incorporation: null,
    commercial_invoice: null,
    waybill: null,
    bill_of_lading_file: null,
};

const INITIAL_NEW_VESSEL = {
    name: '', imo_number: '', type: 'cargo',
    tonnage: '', build_year: '', flag_country: '', owner_name: '',
};

const INITIAL_NEW_CLIENT = {
    full_name: '', email: '', phone: '', company_name: '',
    kra_pin: '', pin: '', role: 'direct_client', password: 'Temp123!@#',
};

const MCI_FEE_RATE = 0.15;

const DEFAULT_BANK_OPTIONS = [
    { value: '', label: 'No bank financing' },
    { value: 'Absa Bank Kenya', label: 'Absa Bank Kenya' },
    { value: 'Bank of Africa Kenya', label: 'Bank of Africa Kenya' },
    { value: 'Bank of Baroda Kenya', label: 'Bank of Baroda Kenya' },
    { value: 'Co-operative Bank of Kenya', label: 'Co-operative Bank of Kenya' },
    { value: 'Diamond Trust Bank Kenya', label: 'Diamond Trust Bank Kenya' },
    { value: 'Equity Bank Kenya', label: 'Equity Bank Kenya' },
    { value: 'Family Bank', label: 'Family Bank' },
    { value: 'Gulf African Bank', label: 'Gulf African Bank' },
    { value: 'I&M Bank Kenya', label: 'I&M Bank Kenya' },
    { value: 'KCB Bank Kenya', label: 'KCB Bank Kenya' },
    { value: 'NCBA Bank Kenya', label: 'NCBA Bank Kenya' },
    { value: 'Prime Bank Kenya', label: 'Prime Bank Kenya' },
    { value: 'SBM Bank Kenya', label: 'SBM Bank Kenya' },
    { value: 'Stanbic Bank Kenya', label: 'Stanbic Bank Kenya' },
    { value: 'Standard Chartered Bank Kenya', label: 'Standard Chartered Bank Kenya' },
];

const SECTIONS = [
    { id: 'vessel', icon: Ship, label: 'Vessel' },
    { id: 'open-cover', icon: Shield, label: 'Open Cover' },
    { id: 'sum-insured', icon: DollarSign, label: 'Sum Insured' },
    { id: 'rates', icon: Percent, label: 'Rates' },
    { id: 'shipment', icon: MapPin, label: 'Shipment' },
    { id: 'cover-details', icon: FileText, label: 'Cover Details' },
    { id: 'dates', icon: Calendar, label: 'Dates' },
    { id: 'client', icon: User, label: 'Client' },
    { id: 'documents', icon: Upload, label: 'Documents' },
];

const SECTION_IDS = SECTIONS.map((s) => s.id);
const REQUIRED_FIELDS = ['vessel_id', 'product_id', 'sum_insured', 'coverage_start', 'coverage_end'];

// =========================================================
// UTILITIES
// =========================================================
function toNumber(value, fallback = 0) {
    if (value === null || value === undefined || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function calculatePremiumLocal(form) {
    const sumInsured = toNumber(form.sum_insured, 0);
    const marineRate = toNumber(form.marine_rate, 0.20);
    const warRate = toNumber(form.war_rate, 0.05);
    const mode = form.mode_of_transport || 'Sea';
    const currency = form.currency || 'USD';

    const marinePremium = sumInsured * (marineRate / 100);
    const warPremium = sumInsured * (warRate / 100);
    const basicPremium = marinePremium + warPremium;
    const mciFee = basicPremium * MCI_FEE_RATE;
    const stampDuty = mode === 'Air' ? (currency === 'KES' ? 40 : 40 / 150) : sumInsured * 0.0005;
    const phcf = basicPremium * 0.0025;
    const trainingLevy = basicPremium * 0.0020;
    const totalPremium = marinePremium + warPremium + mciFee + stampDuty + phcf + trainingLevy;

    return {
        marinePremium,
        warPremium,
        grossPremium: basicPremium,
        mciFee,
        bankInterest: 0,
        stampDuty,
        phcf,
        trainingLevy,
        totalPremium,
    };
}

const reactSelectStyles = (hasError = false) => ({
    control: (provided, state) => ({
        ...provided,
        borderRadius: '16px',
        borderColor: hasError ? '#ef4444' : state.isFocused ? '#3a663f' : '#e5e7eb',
        padding: '4px 8px',
        minHeight: '52px',
        backgroundColor: 'transparent',
        boxShadow: hasError
            ? '0 0 0 4px rgba(239,68,68,0.1)'
            : state.isFocused
                ? '0 0 0 4px rgba(58,102,63,0.12)'
                : 'none',
        '&:hover': { borderColor: hasError ? '#ef4444' : '#3a663f' },
        transition: 'all 0.15s ease',
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: '16px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
        zIndex: 9999,
        overflow: 'hidden',
        marginTop: 6,
        border: '1px solid rgba(0,0,0,0.05)',
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#f0f7f0' : 'transparent',
        color: state.isSelected ? '#ffffff' : state.isFocused ? '#3a663f' : '#1f2937',
        padding: '12px 18px',
        cursor: 'pointer',
        fontSize: '14px',
    }),
    input: (provided) => ({ ...provided, color: '#1f2937' }),
    singleValue: (provided) => ({ ...provided, color: '#1f2937' }),
    placeholder: (provided) => ({ ...provided, color: '#9ca3af' }),
    noOptionsMessage: (provided) => ({ ...provided, color: '#6b7280', padding: '14px' }),
});

const STATIC_RS_STYLE = reactSelectStyles(false);

// =========================================================
// SMALL PURE HELPERS
// =========================================================
function sectionOfError(field) {
    if (field === 'vessel_id' || field === 'product_id') return 'vessel';
    if (field === 'sum_insured' || field === 'currency') return 'sum-insured';
    if (
        field === 'marine_rate' ||
        field === 'war_rate' ||
        field === 'mode_of_transport' ||
        field === 'custom_bank_interest'
    ) {
        return 'rates';
    }
    if (field === 'coverage_start' || field === 'coverage_end') return 'dates';
    return 'vessel';
}

function validateForm(form) {
    const errors = {};
    REQUIRED_FIELDS.forEach((f) => {
        if (!form[f] || String(form[f]).trim() === '') {
            errors[f] = 'This field is required';
        }
    });
    if (form.sum_insured && toNumber(form.sum_insured, 0) <= 0) {
        errors.sum_insured = 'Must be greater than zero';
    }
    if (form.coverage_start && form.coverage_end) {
        if (new Date(form.coverage_end) <= new Date(form.coverage_start)) {
            errors.coverage_end = 'End date must be after start date';
        }
        const start = new Date(form.coverage_start);
        const maxEnd = new Date(start);
        maxEnd.setDate(maxEnd.getDate() + 90);
        const maxStr = maxEnd.toISOString().split('T')[0];
        if (form.coverage_end > maxStr) {
            errors.coverage_end = 'Cannot exceed 90 days';
        }
    }
    return errors;
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function QuoteCreate() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [vessels, setVessels] = useState([]);
    const [products, setProducts] = useState([]);
    const [openCovers, setOpenCovers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(true);

    const [form, setForm] = usePersistedState('quote_draft_form', INITIAL_FORM);

    const [files, setFiles] = useState(INITIAL_FILES);
    const [multipleFiles, setMultipleFiles] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedOpenCover, setSelectedOpenCover] = useState(null);
    const [estimatedPremium, setEstimatedPremium] = useState(null);
    const [loadingPremium, setLoadingPremium] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const [bankOptions, setBankOptions] = usePersistedState(
        'quote_bank_options',
        DEFAULT_BANK_OPTIONS
    );
    const [showAddBankModal, setShowAddBankModal] = useState(false);

    const [clientSearch, setClientSearch] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [searchingClients, setSearchingClients] = useState(false);

    const [showAddVesselModal, setShowAddVesselModal] = useState(false);
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [showReview, setShowReview] = useState(false);

    const [showDraftBanner, setShowDraftBanner] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('quote_draft_form') || '{}');
            return Object.values(saved).some(
                (v) => v && v !== 'USD' && v !== 'Sea' && v !== '0.20' && v !== '0.05'
            );
        } catch {
            return false;
        }
    });

    const activeSection = useScrollSpy(SECTION_IDS);

    // =========================================================
    // INITIAL FETCH
    // =========================================================
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingOptions(true);
            try {
                const [v, p, oc] = await Promise.all([
                    api.get('/vessels').then((r) => r.data).catch(() => []),
                    api.get('/products').then((r) => r.data).catch(() => []),
                    api.get('/quotes/open-covers').then((r) => r.data).catch(() => []),
                ]);
                if (cancelled) return;
                setVessels(v || []);
                setProducts(p || []);
                setOpenCovers(oc || []);
            } finally {
                if (!cancelled) setLoadingOptions(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // =========================================================
    // RE-FETCH OPEN COVERS WHEN THE SELECTED CLIENT CHANGES
    // Any agent/broker can then see covers linked to that client.
    // =========================================================
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const url = form.client_id
                    ? `/quotes/open-covers?client_id=${encodeURIComponent(form.client_id)}`
                    : '/quotes/open-covers';
                const { data } = await api.get(url);
                if (!cancelled) setOpenCovers(data || []);
            } catch (err) {
                console.error('[QuoteCreate] Failed to load open covers:', err);
            }
        })();
        return () => { cancelled = true; };
    }, [form.client_id]);

    // =========================================================
    // CLIENT SEARCH
    // =========================================================
    useEffect(() => {
        if (clientSearch.length < 2 || selectedClient) {
            setClients([]);
            setShowClientDropdown(false);
            setSearchingClients(false);
            return;
        }

        const controller = new AbortController();
        setSearchingClients(true);
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get(
                    `/users?search=${encodeURIComponent(clientSearch)}&role=direct_client`,
                    { signal: controller.signal }
                );
                setClients(data || []);
                setShowClientDropdown(true);
            } catch (err) {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    console.error('[QuoteCreate] Client search failed:', err);
                }
            } finally {
                setSearchingClients(false);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [clientSearch, selectedClient]);

    // =========================================================
    // OPEN COVER SYNC
    // =========================================================
    useEffect(() => {
        if (!form.open_cover_id) {
            setSelectedOpenCover(null);
            return;
        }
        const found = openCovers.find((oc) => String(oc.id) === String(form.open_cover_id));
        setSelectedOpenCover(found || null);
        if (found) {
            setForm((prev) => {
                if (
                    prev.marine_rate === String(found.marine_rate) &&
                    prev.war_rate === String(found.war_rate) &&
                    prev.currency === found.currency &&
                    prev.insured_name === (found.client_name || prev.insured_name)
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    marine_rate: String(found.marine_rate),
                    war_rate: String(found.war_rate),
                    currency: found.currency,
                    insured_name: found.client_name || prev.insured_name,
                };
            });
        }
    }, [form.open_cover_id, openCovers, setForm]);

    // =========================================================
    // RESET OPEN COVER WHEN CLIENT CHANGES
    // =========================================================
    useEffect(() => {
        setForm((prev) =>
            prev.open_cover_id ? { ...prev, open_cover_id: '' } : prev
        );
        setSelectedOpenCover(null);
       
    }, [form.client_id]);

    // =========================================================
    // PREMIUM PREVIEW (debounced)
    // =========================================================
    useEffect(() => {
        const sumInsured = toNumber(form.sum_insured, 0);
        if (!form.sum_insured || sumInsured <= 0) {
            setEstimatedPremium(null);
            setLoadingPremium(false);
            return;
        }

        const controller = new AbortController();
        setLoadingPremium(true);

        const timer = setTimeout(async () => {
            try {
                const { data } = await api.post(
                    '/quotes/preview-premium',
                    {
                        sum_insured: sumInsured,
                        marine_rate: toNumber(form.marine_rate, 0.20),
                        war_rate: toNumber(form.war_rate, 0.05),
                        mode_of_transport: form.mode_of_transport,
                        currency: form.currency,
                    },
                    { signal: controller.signal }
                );
                setEstimatedPremium(data);
            } catch (err) {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    console.warn('[QuoteCreate] Premium preview failed, using local calc:', err.message);
                    setEstimatedPremium(calculatePremiumLocal(form));
                }
            } finally {
                setLoadingPremium(false);
            }
        }, 400);

        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [
        form.sum_insured, form.marine_rate, form.war_rate,
        form.mode_of_transport, form.currency, form,
    ]);

    // =========================================================
    // HANDLERS
    // =========================================================
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
        setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: null } : prev));
        setErrorMsg(null);
    }, [setForm]);

    const handleSelectClient = useCallback((client) => {
        setSelectedClient(client);
        setClientSearch(client.full_name || '');
        setForm((prev) => ({
            ...prev,
            client_id: client.id,
            insured_name: client.full_name || prev.insured_name,
        }));
        setShowClientDropdown(false);
    }, [setForm]);

    const handleClearClient = useCallback(() => {
        setSelectedClient(null);
        setClientSearch('');
        setForm((prev) => ({ ...prev, client_id: '', insured_name: '' }));
    }, [setForm]);

    const handleFileChange = useCallback((e) => {
        const { name, files: fileList } = e.target;
        const file = fileList?.[0];
        if (!file) return;
        setFiles((prev) => ({ ...prev, [name]: file }));
    }, []);

    const handleMultipleFileChange = useCallback((e) => {
        const newFiles = Array.from(e.target.files || []);
        setMultipleFiles((prev) => [...prev, ...newFiles]);
        e.target.value = '';
    }, []);

    const removeSingleFile = useCallback((fieldName) => {
        setFiles((prev) => ({ ...prev, [fieldName]: null }));
    }, []);

    const replaceSingleFile = useCallback((fieldName) => {
        setFiles((prev) => ({ ...prev, [fieldName]: null }));
        setTimeout(() => {
            document.querySelector(`input[name="${fieldName}"]`)?.click();
        }, 50);
    }, []);

    const removeMultipleFile = useCallback((index) => {
        setMultipleFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleAddBank = useCallback((name) => {
        const trimmed = String(name || '').trim();
        if (!trimmed) return;
        setBankOptions((prev) => {
            const exists = prev.some(
                (o) => o.value.toLowerCase() === trimmed.toLowerCase()
            );
            if (exists) return prev;
            return [...prev, { value: trimmed, label: trimmed }].sort((a, b) =>
                a.label.localeCompare(b.label)
            );
        });
        setForm((prev) => ({ ...prev, custom_bank_interest: trimmed }));
    }, [setBankOptions, setForm]);

    const handleSelectBank = useCallback((opt) => {
        setForm((prev) => ({
            ...prev,
            custom_bank_interest: opt?.value || '',
        }));
    }, [setForm]);

    const scrollToSection = useCallback((id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    // =========================================================
    // SUBMIT
    // =========================================================
    const performSubmit = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));

            const namedKeys = [];
            Object.entries(files).forEach(([k, file]) => {
                if (file) {
                    fd.append(k, file);
                    namedKeys.push(k);
                }
            });

            multipleFiles.forEach((file) => fd.append('attachments', file));

            console.log('[QuoteCreate] Submitting with named files:', namedKeys);
            console.log('[QuoteCreate] Submitting with additional files:', multipleFiles.length);

            const resultAction = await dispatch(createQuote(fd));

            if (createQuote.fulfilled.match(resultAction)) {
                localStorage.removeItem('quote_draft_form');
                setForm(INITIAL_FORM);
                setFiles(INITIAL_FILES);
                setMultipleFiles([]);
                navigate('/quotes', {
                    state: {
                        toast: resultAction.payload?.auto_approved
                            ? { type: 'success', message: 'Quote created and auto-approved!' }
                            : { type: 'success', message: 'Quote created successfully' },
                    },
                });
            } else if (createQuote.rejected.match(resultAction)) {
                setErrorMsg(resultAction.payload || 'Quote creation failed. Please try again.');
                setShowReview(false);
            }
        } catch (err) {
            setErrorMsg(err?.message || 'Quote creation failed. Please try again.');
            setShowReview(false);
        } finally {
            setLoading(false);
        }
    }, [dispatch, files, form, multipleFiles, navigate, setForm]);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        setErrorMsg(null);

        const errors = validateForm(form);
        if (Object.keys(errors).length) {
            setFieldErrors(errors);
            setErrorMsg(`Please fix ${Object.keys(errors).length} field(s) before continuing.`);
            const firstField = Object.keys(errors)[0];
            document.getElementById(sectionOfError(firstField))?.scrollIntoView({
                behavior: 'smooth', block: 'start',
            });
            return;
        }

        if (selectedOpenCover) {
            const sumInsured = toNumber(form.sum_insured, 0);
            const limit = toNumber(selectedOpenCover.sum_insured_limit, 0);
            const used = toNumber(selectedOpenCover.used_amount, 0);
            const remaining = limit - used;

            if (sumInsured > limit) {
                setErrorMsg(`Sum insured exceeds Open Cover limit of ${selectedOpenCover.currency} ${limit.toLocaleString()}.`);
                return;
            }
            if (sumInsured > remaining) {
                setErrorMsg(`Insufficient remaining limit (${selectedOpenCover.currency} ${remaining.toLocaleString()}).`);
                return;
            }

            if (
                selectedOpenCover.client_id &&
                form.client_id &&
                String(selectedOpenCover.client_id) !== String(form.client_id)
            ) {
                setErrorMsg('Selected Open Cover belongs to a different client. Please select a matching cover.');
                return;
            }
        }

        setShowReview(true);
    }, [form, selectedOpenCover]);

    // Ctrl/Cmd+Enter -> submit
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                document.getElementById('quote-form')?.requestSubmit();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // =========================================================
    // DERIVED VALUES
    // =========================================================
    const today = useMemo(() => new Date().toISOString().split('T')[0], []);
    const maxDateStr = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 90);
        return d.toISOString().split('T')[0];
    }, []);

    const selectedVessel = useMemo(
        () => vessels.find((v) => String(v.id) === String(form.vessel_id)),
        [vessels, form.vessel_id]
    );

    const vesselOptions = useMemo(
        () => vessels.map((v) => ({
            value: v.id,
            label: `${v.name}${v.imo_number ? ` (${v.imo_number})` : ''}`,
        })),
        [vessels]
    );

    const selectedVesselOption = useMemo(
        () => form.vessel_id
            ? vesselOptions.find((o) => String(o.value) === String(form.vessel_id)) || null
            : null,
        [form.vessel_id, vesselOptions]
    );

    const productOptions = useMemo(
        () => products.map((p) => ({ value: p.id, label: p.name })),
        [products]
    );

    const selectedProductOption = useMemo(
        () => form.product_id
            ? productOptions.find((o) => String(o.value) === String(form.product_id)) || null
            : null,
        [form.product_id, productOptions]
    );

    const selectedBankOption = useMemo(() => {
        if (!form.custom_bank_interest) return null;
        return bankOptions.find((o) => o.value === form.custom_bank_interest)
            || { value: form.custom_bank_interest, label: form.custom_bank_interest };
    }, [bankOptions, form.custom_bank_interest]);

    // Covers matching the selected client (only active ones)
    const eligibleOpenCovers = useMemo(() => {
        const active = openCovers.filter((oc) => oc.status === 'active');
        if (!form.client_id) return active;
        return active.filter(
            (oc) => !oc.client_id || String(oc.client_id) === String(form.client_id)
        );
    }, [openCovers, form.client_id]);

    const errorSections = useMemo(() => {
        const set = new Set();
        Object.keys(fieldErrors).forEach((f) => set.add(sectionOfError(f)));
        return set;
    }, [fieldErrors]);

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Left rail */}
                <aside className="hidden lg:block fixed left-6 top-1/2 -translate-y-1/2 z-20">
                    <nav className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-xl shadow-emerald-900/5 border border-gray-200/70 dark:border-slate-800 p-2.5 space-y-1">
                        {SECTIONS.map(({ id, icon: Icon, label }) => {
                            const isActive = activeSection === id;
                            const hasError = errorSections.has(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => scrollToSection(id)}
                                    className={`group relative w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-medium transition-all ${isActive
                                            ? 'bg-gradient-to-r from-[#2d5232] to-[#3a663f] text-white shadow-lg shadow-emerald-900/20'
                                            : hasError
                                                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30'
                                                : 'text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-800 dark:hover:text-white'
                                        }`}
                                    title={label}
                                    aria-current={isActive ? 'step' : undefined}
                                >
                                    <Icon className="w-4 h-4 flex-shrink-0" />
                                    <span className="hidden xl:inline">{label}</span>
                                    {hasError && (
                                        <span className="absolute right-2 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                <div className="lg:ml-20 xl:mr-80 space-y-6">

                    {showDraftBanner && (
                        <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/20 border border-amber-200 dark:border-amber-900/50 rounded-2xl">
                            <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <p className="text-sm text-amber-800 dark:text-amber-300 flex-1">
                                We restored your previous draft — pick up where you left off.
                            </p>
                            <button
                                type="button"
                                onClick={() => { setForm(INITIAL_FORM); setShowDraftBanner(false); }}
                                className="text-xs font-semibold text-amber-700 dark:text-amber-300 hover:underline"
                            >
                                Clear draft
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowDraftBanner(false)}
                                className="text-amber-600 hover:text-amber-800 p-1 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40"
                                aria-label="Dismiss"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Hero */}
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#14291a] via-[#1e3b29] to-[#3a663f] p-8 md:p-10 text-white shadow-2xl shadow-emerald-950/20">
                        <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-400/10 rounded-full blur-3xl" />
                        <div className="absolute -bottom-32 -left-16 w-96 h-96 bg-emerald-300/10 rounded-full blur-3xl" />
                        <div
                            className="absolute inset-0 opacity-[0.07]"
                            style={{
                                backgroundImage:
                                    'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                                backgroundSize: '24px 24px',
                            }}
                        />

                        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div className="max-w-2xl">
                                <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/15">
                                    <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
                                    <span className="text-emerald-100 text-[11px] uppercase tracking-[0.15em] font-semibold">
                                        New Marine Quote
                                    </span>
                                </div>
                                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                                    Create Marine Cargo Quote
                                </h1>
                                <p className="text-emerald-100/80 mt-3 text-sm md:text-base leading-relaxed">
                                    Fill in the shipment details below. If the client has an active open cover,
                                    attach it and the quote will be auto-approved instantly.
                                </p>
                            </div>
                            <div className="flex flex-col items-start md:items-end gap-2">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                                    <Shield className="w-4 h-4 text-emerald-300" />
                                    <span className="text-sm font-medium">Instant Approval</span>
                                </div>
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-xl border border-white/15">
                                    <Lock className="w-4 h-4 text-emerald-300" />
                                    <span className="text-sm font-medium">Encrypted Upload</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form id="quote-form" onSubmit={handleSubmit} className="space-y-6" noValidate>

                        {errorMsg && (
                            <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-2xl text-red-700 dark:text-red-300">
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <span className="text-sm flex-1 leading-relaxed">{errorMsg}</span>
                                <button
                                    type="button"
                                    onClick={() => setErrorMsg(null)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40"
                                    aria-label="Dismiss error"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Vessel & Product */}
                        <FormSection id="vessel" icon={Ship} title="Vessel & Product" defaultOpen>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                                            Vessel <span className="text-red-500">*</span>
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowAddVesselModal(true)}
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-emerald-400 hover:text-brand-700 px-2.5 py-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            Add Vessel
                                        </button>
                                    </div>

                                    {loadingOptions ? (
                                        <div className="h-[52px] bg-gray-100 dark:bg-slate-800 rounded-2xl animate-pulse flex items-center px-4 gap-2">
                                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                            <span className="text-xs text-gray-400">Loading vessels…</span>
                                        </div>
                                    ) : (
                                        <Select
                                            value={selectedVesselOption}
                                            onChange={(opt) =>
                                                handleChange({ target: { name: 'vessel_id', value: opt?.value || '' } })
                                            }
                                            options={vesselOptions}
                                            isSearchable
                                            isClearable
                                            placeholder="Search vessel…"
                                            classNamePrefix="react-select"
                                            styles={reactSelectStyles(!!fieldErrors.vessel_id)}
                                            noOptionsMessage={() => 'No vessels found'}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            menuShouldScrollIntoView={false}
                                        />
                                    )}
                                    {fieldErrors.vessel_id && (
                                        <p className="mt-2 text-xs text-red-500 font-medium">{fieldErrors.vessel_id}</p>
                                    )}
                                    {selectedVessel && !fieldErrors.vessel_id && (
                                        <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50 rounded-lg">
                                            <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                                            <span className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                                                {selectedVessel.name}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Insurance Product <span className="text-red-500">*</span>
                                    </label>
                                    {loadingOptions ? (
                                        <div className="h-[52px] bg-gray-100 dark:bg-slate-800 rounded-2xl animate-pulse" />
                                    ) : (
                                        <Select
                                            value={selectedProductOption}
                                            onChange={(opt) =>
                                                handleChange({ target: { name: 'product_id', value: opt?.value || '' } })
                                            }
                                            options={productOptions}
                                            isSearchable
                                            isClearable
                                            placeholder="Search product…"
                                            classNamePrefix="react-select"
                                            styles={reactSelectStyles(!!fieldErrors.product_id)}
                                            noOptionsMessage={() => 'No products found'}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                            menuShouldScrollIntoView={false}
                                        />
                                    )}
                                    {fieldErrors.product_id && (
                                        <p className="mt-2 text-xs text-red-500 font-medium">{fieldErrors.product_id}</p>
                                    )}
                                </div>
                            </div>
                        </FormSection>

                        {/* Open Cover — ALWAYS rendered */}
                        <FormSection id="open-cover" icon={Shield} title="Marine Open Cover">
                            {eligibleOpenCovers.length === 0 ? (
                                <div className="flex items-start gap-3 p-5 bg-gradient-to-br from-gray-50 to-slate-50 dark:from-slate-900/60 dark:to-slate-800/40 rounded-2xl border border-dashed border-gray-200 dark:border-slate-700">
                                    <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                                        <Info className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-700 dark:text-slate-300 text-sm">
                                            No open covers available
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 leading-relaxed">
                                            {form.client_id
                                                ? 'The selected client has no active open covers. You can still submit this quote with manual rates.'
                                                : 'Select a client above to see their active open covers — or proceed with manual rates.'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <SelectField
                                        label="Link to Open Cover (optional — auto-approves quote)"
                                        name="open_cover_id"
                                        value={form.open_cover_id}
                                        onChange={handleChange}
                                        required={false}
                                        options={[
                                            { value: '', label: 'None — Use manual rates' },
                                            ...eligibleOpenCovers.map((oc) => ({
                                                value: oc.id,
                                                label: `${oc.cover_number} — ${oc.client_name} (${oc.currency} ${Number(oc.sum_insured_limit).toLocaleString()})`,
                                            })),
                                        ]}
                                    />

                                    {selectedOpenCover && (() => {
                                        const remaining =
                                            toNumber(selectedOpenCover.sum_insured_limit, 0) -
                                            toNumber(selectedOpenCover.used_amount, 0);
                                        const usedPercent =
                                            toNumber(selectedOpenCover.sum_insured_limit, 0) > 0
                                                ? (toNumber(selectedOpenCover.used_amount, 0) /
                                                    toNumber(selectedOpenCover.sum_insured_limit, 1)) * 100
                                                : 0;
                                        const currentQuote = toNumber(form.sum_insured, 0);
                                        const wouldExceed = currentQuote > remaining;

                                        return (
                                            <div className="mt-4 rounded-2xl overflow-hidden border border-blue-200 dark:border-blue-900/50">
                                                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Shield className="w-4 h-4 text-white" />
                                                        <span className="font-bold text-white text-sm tracking-wide">
                                                            Open Cover Active
                                                        </span>
                                                    </div>
                                                    <span className="px-2.5 py-0.5 bg-white/20 backdrop-blur-sm text-white text-[10px] uppercase tracking-wider font-bold rounded-full">
                                                        Auto-Approval On
                                                    </span>
                                                </div>
                                                <div className="bg-blue-50/60 dark:bg-blue-950/20 p-5 space-y-4">
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <CoverStat label="Cover No." value={selectedOpenCover.cover_number} />
                                                        <CoverStat label="Client" value={selectedOpenCover.client_name} />
                                                        <CoverStat
                                                            label="Total Limit"
                                                            value={`${selectedOpenCover.currency} ${Number(selectedOpenCover.sum_insured_limit).toLocaleString()}`}
                                                        />
                                                        <CoverStat
                                                            label="Remaining"
                                                            value={`${selectedOpenCover.currency} ${remaining.toLocaleString()}`}
                                                            tone={wouldExceed ? 'danger' : 'success'}
                                                        />
                                                    </div>

                                                    <div>
                                                        <div className="flex justify-between text-xs mb-1.5">
                                                            <span className="text-blue-700/70 dark:text-blue-300/70 font-medium">
                                                                Usage
                                                            </span>
                                                            <span className="text-blue-800 dark:text-blue-200 font-semibold tabular-nums">
                                                                {usedPercent.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 bg-blue-200/60 dark:bg-blue-900/40 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full transition-all duration-700 ${usedPercent > 80
                                                                        ? 'bg-gradient-to-r from-red-500 to-rose-500'
                                                                        : usedPercent > 50
                                                                            ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                                                            : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                                                                    }`}
                                                                style={{ width: `${Math.min(usedPercent, 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {wouldExceed ? (
                                                        <p className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-semibold text-xs">
                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                            This quote would exceed the remaining limit by{' '}
                                                            {selectedOpenCover.currency}{' '}
                                                            {(currentQuote - remaining).toLocaleString()}
                                                        </p>
                                                    ) : (
                                                        <p className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium text-xs">
                                                            <CheckCircle className="w-3.5 h-3.5" />
                                                            Linked quotes are <strong>automatically approved</strong>.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </>
                            )}
                        </FormSection>

                        {/* Sum Insured */}
                        <FormSection id="sum-insured" icon={DollarSign} title="Sum Insured">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Sum Insured <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 font-semibold pointer-events-none">
                                            {form.currency === 'USD' ? '$' : 'KSh'}
                                        </span>
                                        <input
                                            type="number"
                                            name="sum_insured"
                                            value={form.sum_insured}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            min="1"
                                            step="any"
                                            className={`w-full border rounded-2xl pl-12 pr-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 dark:bg-slate-800/60 dark:text-white text-sm font-medium ${fieldErrors.sum_insured
                                                    ? 'border-red-400'
                                                    : 'border-gray-200 dark:border-slate-700'
                                                }`}
                                        />
                                    </div>
                                    {fieldErrors.sum_insured && (
                                        <p className="mt-2 text-xs text-red-500 font-medium">{fieldErrors.sum_insured}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Currency <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="currency"
                                        value={form.currency}
                                        onChange={handleChange}
                                        disabled={!!selectedOpenCover}
                                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
                                    >
                                        <option value="USD">USD ($)</option>
                                        <option value="KES">KES (KSh)</option>
                                    </select>
                                    {selectedOpenCover && (
                                        <p className="text-xs text-gray-400 mt-1.5 inline-flex items-center gap-1">
                                            <Lock className="w-3 h-3" /> Locked by Open Cover
                                        </p>
                                    )}
                                </div>
                            </div>
                        </FormSection>

                        {/* Rates */}
                        <FormSection id="rates" icon={Percent} title="Premium Rates, Bank & Transport">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Mode of Transport <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="mode_of_transport"
                                        value={form.mode_of_transport}
                                        onChange={handleChange}
                                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm font-medium"
                                    >
                                        <option value="Sea">🚢 Sea</option>
                                        <option value="Air">✈️ Air</option>
                                        <option value="Road">🚚 Road</option>
                                        <option value="Rail">🚆 Rail</option>
                                    </select>
                                </div>
                                <InputField
                                    label="Marine Rate (%)"
                                    name="marine_rate"
                                    value={form.marine_rate}
                                    onChange={handleChange}
                                    type="number"
                                    step="0.01"
                                    disabled={!!selectedOpenCover}
                                />
                                <InputField
                                    label="War Rate (%)"
                                    name="war_rate"
                                    value={form.war_rate}
                                    onChange={handleChange}
                                    type="number"
                                    step="0.01"
                                    disabled={!!selectedOpenCover}
                                />

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                                            <Landmark className="w-3.5 h-3.5" />
                                            Bank / Financial Institution
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowAddBankModal(true)}
                                            className="text-xs font-semibold text-brand-600 dark:text-emerald-400 hover:text-brand-700 p-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                            title="Add a bank"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <Select
                                        value={selectedBankOption}
                                        onChange={handleSelectBank}
                                        options={bankOptions}
                                        isSearchable
                                        isClearable
                                        placeholder="Select bank…"
                                        classNamePrefix="react-select"
                                        styles={STATIC_RS_STYLE}
                                        noOptionsMessage={() => 'No banks'}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        menuShouldScrollIntoView={false}
                                    />
                                </div>
                            </div>
                            {selectedOpenCover && (
                                <p className="text-xs text-gray-400 mt-3 inline-flex items-center gap-1.5">
                                    <Lock className="w-3 h-3" />
                                    Marine & War rates locked by Open Cover
                                </p>
                            )}
                        </FormSection>

                        {/* Shipment */}
                        <FormSection id="shipment" icon={MapPin} title="Shipment Details">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <InputField
                                    label="From (Origin)"
                                    name="from_location"
                                    value={form.from_location}
                                    onChange={handleChange}
                                    placeholder="e.g., Shanghai, China"
                                    required={false}
                                />
                                <InputField
                                    label="To (Destination)"
                                    name="to_location"
                                    value={form.to_location}
                                    onChange={handleChange}
                                    placeholder="e.g., Mombasa Port"
                                    required={false}
                                />
                                <InputField
                                    label="Bill of Lading (Reference)"
                                    name="bill_of_lading"
                                    value={form.bill_of_lading}
                                    onChange={handleChange}
                                    placeholder="e.g., MEDUYF896045"
                                    required={false}
                                />
                                <InputField
                                    label="IDF Number"
                                    name="invoice_number"
                                    value={form.invoice_number}
                                    onChange={handleChange}
                                    placeholder="IDF reference"
                                    required={false}
                                />
                            </div>
                        </FormSection>

                        {/* Cover Details */}
                        <FormSection id="cover-details" icon={FileText} title="Cover Details">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Interests Insured
                                    </label>
                                    <textarea
                                        name="interests_insured"
                                        value={form.interests_insured}
                                        onChange={handleChange}
                                        rows={5}
                                        placeholder={"e.g., Institute cargo clauses (A)\nInstitute war clauses (cargo)\nInstitute strikes clauses"}
                                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 resize-none text-sm leading-relaxed"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                        Comments / Conditions
                                    </label>
                                    <textarea
                                        name="comments_conditions"
                                        value={form.comments_conditions}
                                        onChange={handleChange}
                                        rows={5}
                                        placeholder={"e.g., Excluding Breakage\n10% each and every loss minimum Kshs. 30,000/-"}
                                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 resize-none text-sm leading-relaxed"
                                    />
                                </div>
                            </div>
                            <div className="mt-5">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                    Other Terms & Conditions
                                </label>
                                <textarea
                                    name="other_terms"
                                    value={form.other_terms}
                                    onChange={handleChange}
                                    rows={3}
                                    placeholder="Other terms and conditions as per open cover…"
                                    className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 resize-none text-sm leading-relaxed"
                                />
                            </div>
                            <div className="mt-5">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                                    Insured Name
                                </label>
                                <input
                                    type="text"
                                    name="insured_name"
                                    value={form.insured_name}
                                    onChange={handleChange}
                                    placeholder="Company or individual name"
                                    className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                />
                            </div>
                        </FormSection>

                        {estimatedPremium && (
                            <PremiumBreakdown
                                premium={estimatedPremium}
                                currency={form.currency}
                                marineRate={form.marine_rate}
                                warRate={form.war_rate}
                                mode={form.mode_of_transport}
                                loading={loadingPremium}
                            />
                        )}

                        {/* Dates */}
                        <FormSection id="dates" icon={Calendar} title="Coverage Period (Max 90 Days)">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <InputField
                                    label="Coverage Start *"
                                    name="coverage_start"
                                    value={form.coverage_start}
                                    onChange={(e) => {
                                        const startDate = e.target.value;
                                        handleChange(e);
                                        if (startDate) {
                                            const end = new Date(startDate);
                                            end.setDate(end.getDate() + 90);
                                            const endStr = end.toISOString().split('T')[0];
                                            handleChange({ target: { name: 'coverage_end', value: endStr } });
                                        } else {
                                            handleChange({ target: { name: 'coverage_end', value: '' } });
                                        }
                                    }}
                                    type="date"
                                    min={today}
                                />
                                <InputField
                                    label="Coverage End (Auto: 90 Days) *"
                                    name="coverage_end"
                                    value={form.coverage_end}
                                    type="date"
                                    readOnly
                                    disabled
                                    min={form.coverage_start || today}
                                    max={maxDateStr}
                                />
                            </div>
                            {fieldErrors.coverage_end && (
                                <p className="mt-2 text-xs text-red-500 font-medium">{fieldErrors.coverage_end}</p>
                            )}
                        </FormSection>

                        {/* Client */}
                        <FormSection id="client" icon={User} title="Client Information">
                            <div className="relative">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                                        Client <span className="text-gray-400 text-xs font-normal">(optional — search & select)</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddClientModal(true)}
                                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-emerald-400 hover:text-brand-700 px-2.5 py-1 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                                    >
                                        <UserPlus className="w-3.5 h-3.5" />
                                        Add Client
                                    </button>
                                </div>

                                {selectedClient ? (
                                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-200 dark:border-emerald-900/50 rounded-2xl">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-emerald-900/20 flex-shrink-0">
                                                {selectedClient.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-900 dark:text-white truncate">
                                                    {selectedClient.full_name}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                    {selectedClient.email}
                                                </p>
                                                {selectedClient.company_name && (
                                                    <p className="text-xs text-gray-400 dark:text-slate-500 truncate">
                                                        {selectedClient.company_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleClearClient}
                                            className="ml-3 text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                                            aria-label="Clear client"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="relative">
                                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                                            <input
                                                type="text"
                                                value={clientSearch}
                                                onChange={(e) => setClientSearch(e.target.value)}
                                                onFocus={() => clients.length > 0 && setShowClientDropdown(true)}
                                                placeholder="Search client by name or email…"
                                                className="w-full pl-12 pr-10 py-3.5 border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm"
                                            />
                                            {searchingClients && (
                                                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
                                            )}
                                        </div>

                                        {showClientDropdown && (
                                            <div className="absolute z-30 mt-2 w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 max-h-64 overflow-y-auto">
                                                {clients.length === 0 && !searchingClients && (
                                                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-slate-400">
                                                        No clients found
                                                    </div>
                                                )}
                                                {clients.map((client) => (
                                                    <button
                                                        key={client.id}
                                                        type="button"
                                                        onClick={() => handleSelectClient(client)}
                                                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/60 dark:hover:bg-emerald-950/20 transition-colors text-left border-b border-gray-100 dark:border-slate-700/60 last:border-0"
                                                    >
                                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                                                            {client.full_name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                                {client.full_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                                                {client.email}
                                                            </p>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!selectedClient && (
                                    <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
                                        Leave blank if the quote is for yourself (direct client).
                                    </p>
                                )}
                            </div>
                        </FormSection>

                        <DocumentsSection
                            files={files}
                            multipleFiles={multipleFiles}
                            onFileChange={handleFileChange}
                            onMultipleFileChange={handleMultipleFileChange}
                            onRemoveSingle={removeSingleFile}
                            onReplaceSingle={replaceSingleFile}
                            onRemoveMultiple={removeMultipleFile}
                        />

                        {/* Submit */}
                        <div className="sticky bottom-4 z-10">
                            <button
                                type="submit"
                                disabled={loading}
                                className="group w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-bold py-4 rounded-2xl shadow-xl shadow-emerald-900/25 hover:shadow-2xl transition-all duration-300 flex items-center justify-center gap-3 transform active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Generating Quote…
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                                        Review & Generate Quote
                                        <kbd className="hidden sm:inline-block ml-2 text-[10px] px-2 py-0.5 bg-white/20 rounded-md font-mono">
                                            ⌘↵
                                        </kbd>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Right summary rail */}
                <aside className="hidden xl:block fixed right-8 top-32 w-72 z-10">
                    <SummaryPanel
                        form={form}
                        vessel={selectedVessel}
                        client={selectedClient}
                        openCover={selectedOpenCover}
                        premium={estimatedPremium}
                    />
                </aside>
            </div>

            {showAddVesselModal && (
                <AddVesselModal
                    show={showAddVesselModal}
                    onClose={() => setShowAddVesselModal(false)}
                    onCreated={(vessel) => {
                        setVessels((prev) => [...prev, vessel]);
                        setForm((prev) => ({ ...prev, vessel_id: vessel.id }));
                        setShowAddVesselModal(false);
                    }}
                />
            )}

            {showAddClientModal && (
                <AddClientModal
                    show={showAddClientModal}
                    onClose={() => setShowAddClientModal(false)}
                    onCreated={(client) => {
                        setClients((prev) => [...prev, client]);
                        handleSelectClient(client);
                        setShowAddClientModal(false);
                    }}
                />
            )}

            {showAddBankModal && (
                <AddBankModal
                    existing={bankOptions}
                    onClose={() => setShowAddBankModal(false)}
                    onAdd={(name) => {
                        handleAddBank(name);
                        setShowAddBankModal(false);
                    }}
                />
            )}

            {showReview && (
                <ReviewModal
                    open={showReview}
                    onClose={() => setShowReview(false)}
                    onConfirm={performSubmit}
                    submitting={loading}
                    form={form}
                    vessel={selectedVessel}
                    client={selectedClient}
                    openCover={selectedOpenCover}
                    premium={estimatedPremium}
                />
            )}
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

const FormSection = memo(function FormSection({ id, icon: Icon, title, children, defaultOpen = false }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div
            id={id}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm hover:shadow-md border border-gray-200 dark:border-slate-800 scroll-mt-24 transition-shadow"
        >
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 md:p-6 hover:bg-gray-50/70 dark:hover:bg-slate-800/50 transition-colors rounded-3xl text-left"
            >
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-950/50 dark:to-teal-950/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#3a663f] dark:text-emerald-400" />
                    </div>
                    <h3 className="font-bold text-gray-900 dark:text-white">{title}</h3>
                </div>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
            </button>
            {isOpen && (
                <div className="px-5 md:px-6 pb-6 pt-0 border-t border-gray-100 dark:border-slate-800/70">
                    <div className="pt-5 space-y-4">{children}</div>
                </div>
            )}
        </div>
    );
});

const InputField = memo(function InputField({
    label, name, value, onChange, type = 'text',
    placeholder = '', min, max, step, disabled,
    required = true, readOnly = false,
}) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            />
        </div>
    );
});

const SelectField = memo(function SelectField({ label, name, value, onChange, options, disabled, required = true }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {label}
            </label>
            <select
                name={name}
                value={value}
                onChange={onChange}
                disabled={disabled}
                required={required}
                className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3.5 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
            >
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
});

function CoverStat({ label, value, tone = 'default' }) {
    const toneClass =
        tone === 'danger'
            ? 'text-red-600 dark:text-red-400'
            : tone === 'success'
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-blue-800 dark:text-blue-200';
    return (
        <div>
            <p className="text-[10px] uppercase tracking-wider text-blue-600/70 dark:text-blue-400/70 font-bold">
                {label}
            </p>
            <p className={`font-semibold text-sm mt-0.5 truncate ${toneClass}`}>{value}</p>
        </div>
    );
}

const FileInput = memo(function FileInput({ label, name, file, onChange, onRemove, onReplace }) {
    const hasFile = Boolean(file);

    const handlePreview = useCallback(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, [file]);

    const handleDownload = useCallback(() => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                {label}
            </label>

            {hasFile ? (
                <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded-2xl px-3 py-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 dark:text-slate-300 truncate" title={file.name}>
                            {file.name}
                        </p>
                        <p className="text-xs text-gray-400">
                            {(file.size / 1024).toFixed(1)} KB
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handlePreview}
                        title="Preview"
                        className="text-blue-500 hover:text-blue-700 p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        title="Download"
                        className="text-gray-500 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onReplace}
                        title="Replace"
                        className="text-amber-500 hover:text-amber-700 p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors"
                    >
                        <Upload className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onRemove}
                        title="Remove"
                        className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <input
                    type="file"
                    name={name}
                    onChange={onChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-emerald-100 dark:file:bg-emerald-950/40 file:text-[#3a663f] dark:file:text-emerald-400 hover:file:bg-emerald-200 dark:hover:file:bg-emerald-950/60 file:transition-colors file:cursor-pointer"
                />
            )}
        </div>
    );
});

const LocalFileRow = memo(function LocalFileRow({ file, onRemove }) {
    const handlePreview = useCallback(() => {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, [file]);

    const handleDownload = useCallback(() => {
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [file]);

    return (
        <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-800/60 rounded-xl px-3 py-2.5 text-xs border border-gray-100 dark:border-slate-700/60">
            <span className="text-gray-600 dark:text-slate-400 truncate flex-1">
                📄 {file.name}{' '}
                <span className="text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
            </span>
            <button
                type="button"
                onClick={handlePreview}
                className="text-blue-500 hover:text-blue-700 p-1 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950/30"
                aria-label="Preview"
            >
                <Eye className="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                onClick={handleDownload}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
                aria-label="Download"
            >
                <Download className="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                onClick={onRemove}
                className="text-red-400 hover:text-red-600 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                aria-label="Remove"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
});

const DocumentsSection = memo(function DocumentsSection({
    files,
    multipleFiles,
    onFileChange,
    onMultipleFileChange,
    onRemoveSingle,
    onReplaceSingle,
    onRemoveMultiple,
}) {
    return (
        <FormSection id="documents" icon={Upload} title="Document Attachments">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FileInput
                    label="KRA PIN Certificate"
                    name="kra_pin"
                    file={files.kra_pin}
                    onChange={onFileChange}
                    onRemove={() => onRemoveSingle('kra_pin')}
                    onReplace={() => onReplaceSingle('kra_pin')}
                />
                <FileInput
                    label="Certificate of Incorporation"
                    name="certificate_incorporation"
                    file={files.certificate_incorporation}
                    onChange={onFileChange}
                    onRemove={() => onRemoveSingle('certificate_incorporation')}
                    onReplace={() => onReplaceSingle('certificate_incorporation')}
                />
                <FileInput
                    label="Commercial Invoice"
                    name="commercial_invoice"
                    file={files.commercial_invoice}
                    onChange={onFileChange}
                    onRemove={() => onRemoveSingle('commercial_invoice')}
                    onReplace={() => onReplaceSingle('commercial_invoice')}
                />
                <FileInput
                    label="Waybill & IDF Attachment"
                    name="waybill"
                    file={files.waybill}
                    onChange={onFileChange}
                    onRemove={() => onRemoveSingle('waybill')}
                    onReplace={() => onReplaceSingle('waybill')}
                />
                <FileInput
                    label="Bill of Lading (File)"
                    name="bill_of_lading_file"
                    file={files.bill_of_lading_file}
                    onChange={onFileChange}
                    onRemove={() => onRemoveSingle('bill_of_lading_file')}
                    onReplace={() => onReplaceSingle('bill_of_lading_file')}
                />
            </div>

            <div className="mt-5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                    Additional Documents
                    <span className="text-gray-400 text-xs font-normal ml-1.5">(multiple files)</span>
                </label>
                <input
                    type="file"
                    multiple
                    onChange={onMultipleFileChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-emerald-100 dark:file:bg-emerald-950/40 file:text-[#3a663f] dark:file:text-emerald-400 hover:file:bg-emerald-200 dark:hover:file:bg-emerald-950/60 file:transition-colors file:cursor-pointer"
                />

                {multipleFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                        <p className="text-xs text-gray-500 font-medium">
                            {multipleFiles.length} file(s) selected:
                        </p>
                        {multipleFiles.map((file, index) => (
                            <LocalFileRow
                                key={`${file.name}-${index}`}
                                file={file}
                                onRemove={() => onRemoveMultiple(index)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </FormSection>
    );
});

const PremiumBreakdown = memo(function PremiumBreakdown({
    premium, currency, marineRate, warRate, mode, loading,
}) {
    const fmt = (v) =>
        new Intl.NumberFormat(undefined, { minimumFractionDigits: 2 }).format(v || 0);

    return (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border border-emerald-100 dark:border-emerald-900/50 p-6">
            <div className="absolute -top-16 -right-16 w-40 h-40 bg-emerald-200/30 dark:bg-emerald-900/20 rounded-full blur-2xl" />
            <div className="relative">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                            <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <p className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-bold">
                                Premium Breakdown
                            </p>
                            <p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                                Currency: {currency}
                            </p>
                        </div>
                    </div>
                    {loading && <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />}
                </div>

                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <PremiumRow
                            label="Marine Premium"
                            sublabel={`${marineRate}% of sum insured`}
                            value={premium.marinePremium}
                            currency={currency}
                            fmt={fmt}
                        />
                        <PremiumRow
                            label="War Premium"
                            sublabel={`${warRate}% of sum insured`}
                            value={premium.warPremium}
                            currency={currency}
                            fmt={fmt}
                        />
                    </div>

                    <div className="border-t border-emerald-200/60 dark:border-emerald-900/40 pt-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
                                Basic Premium
                            </span>
                            <span className="font-bold text-emerald-900 dark:text-emerald-200 tabular-nums">
                                {currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.grossPremium)}
                            </span>
                        </div>
                    </div>

                    <PremiumRow
                        label="MCI Fee"
                        sublabel="15% of basic premium"
                        value={premium.mciFee}
                        currency={currency}
                        fmt={fmt}
                        highlight
                    />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <PremiumRow
                            label={`Stamp Duty ${mode === 'Air' ? '(flat)' : '(0.05%)'}`}
                            value={premium.stampDuty}
                            currency={currency}
                            fmt={fmt}
                        />
                        <PremiumRow
                            label="PHCF (0.25%)"
                            value={premium.phcf}
                            currency={currency}
                            fmt={fmt}
                        />
                        <PremiumRow
                            label="Training Levy (0.20%)"
                            value={premium.trainingLevy}
                            currency={currency}
                            fmt={fmt}
                        />
                    </div>

                    <div className="border-t-2 border-emerald-300/60 dark:border-emerald-700/40 pt-4 mt-1">
                        <div className="flex justify-between items-center">
                            <span className="text-base font-bold text-emerald-800 dark:text-emerald-300 uppercase tracking-wide">
                                Total Premium
                            </span>
                            <span className="text-2xl font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
                                {currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.totalPremium)}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const PremiumRow = memo(function PremiumRow({ label, sublabel, value, currency, fmt, highlight }) {
    return (
        <div className="flex justify-between items-start gap-2">
            <div className="min-w-0">
                <p className={`text-sm ${highlight ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'text-emerald-800/80 dark:text-emerald-300/80'}`}>
                    {label}
                </p>
                {sublabel && (
                    <p className="text-xs text-emerald-600/60 dark:text-emerald-400/60">{sublabel}</p>
                )}
            </div>
            <p className={`font-semibold tabular-nums whitespace-nowrap ${highlight ? 'text-emerald-700 dark:text-emerald-400' : 'text-emerald-900 dark:text-emerald-200'}`}>
                {currency === 'KES' ? 'KSh ' : '$'}{fmt(value)}
            </p>
        </div>
    );
});

const SummaryPanel = memo(function SummaryPanel({ form, vessel, client, openCover, premium }) {
    const fmt = (v) =>
        new Intl.NumberFormat(undefined, { minimumFractionDigits: 2 }).format(v || 0);

    return (
        <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-xl shadow-emerald-900/5 border border-gray-200/70 dark:border-slate-800 p-5 space-y-4">
            <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Live Summary
                </h3>
            </div>

            <div className="space-y-3 text-sm">
                <SummaryRow label="Vessel" value={vessel?.name || '—'} />
                <SummaryRow
                    label="Route"
                    value={
                        form.from_location && form.to_location
                            ? `${form.from_location} → ${form.to_location}`
                            : '—'
                    }
                />
                <SummaryRow label="Mode" value={form.mode_of_transport} />
                <SummaryRow
                    label="Sum Insured"
                    value={
                        form.sum_insured
                            ? `${form.currency === 'KES' ? 'KSh' : '$'} ${Number(form.sum_insured).toLocaleString()}`
                            : '—'
                    }
                />
                {openCover && (
                    <SummaryRow label="Open Cover" value={openCover.cover_number} highlight />
                )}
                {client && <SummaryRow label="Client" value={client.full_name} />}
                {form.custom_bank_interest && (
                    <SummaryRow label="Bank" value={form.custom_bank_interest} />
                )}
                <SummaryRow
                    label="Period"
                    value={
                        form.coverage_start && form.coverage_end
                            ? `${form.coverage_start} → ${form.coverage_end}`
                            : '—'
                    }
                />
            </div>

            {premium && (
                <div className="pt-4 border-t border-gray-100 dark:border-slate-800">
                    <div className="flex justify-between items-baseline">
                        <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-slate-400 font-bold">
                            Total Premium
                        </span>
                        <span className="text-lg font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">
                            {form.currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.totalPremium)}
                        </span>
                    </div>
                </div>
            )}

            {openCover && (
                <div className="pt-3 border-t border-gray-100 dark:border-slate-800">
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 font-medium">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Auto-approval enabled
                    </p>
                </div>
            )}
        </div>
    );
});

const SummaryRow = memo(function SummaryRow({ label, value, highlight }) {
    return (
        <div className="flex justify-between items-start gap-3">
            <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider flex-shrink-0 mt-0.5 font-semibold">
                {label}
            </span>
            <span className={`text-sm text-right truncate ${highlight ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'font-medium text-gray-800 dark:text-white'}`}>
                {value}
            </span>
        </div>
    );
});

// =========================================================
// MODALS
// =========================================================

function ModalShell({ onClose, title, icon: Icon, children, maxWidth = 'max-w-2xl' }) {
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
            <div className={`bg-white dark:bg-slate-900 rounded-3xl shadow-2xl ${maxWidth} w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 border border-white/20 dark:border-slate-800`}>
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {Icon && (
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-950/50 dark:to-teal-950/30 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-[#3a663f] dark:text-emerald-400" />
                            </div>
                        )}
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function ModalAlert({ type, children }) {
    const isError = type === 'error';
    return (
        <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl mb-4 text-sm border ${isError
                ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400'
                : 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
            }`}>
            {isError ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
            {children}
        </div>
    );
}

function ModalInput({ label, name, value, onChange, placeholder, type = 'text', mono = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all hover:border-gray-300 dark:hover:border-slate-600 text-sm ${mono ? 'font-mono' : ''}`}
            />
        </div>
    );
}

function ModalFooter({ onClose, saving, submitLabel }) {
    return (
        <div className="flex gap-3 pt-5 border-t border-gray-100 dark:border-slate-800">
            <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 py-3 px-4 border border-gray-200 dark:border-slate-700 rounded-2xl text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-semibold text-sm disabled:opacity-50"
            >
                Cancel
            </button>
            <button
                type="submit"
                disabled={saving}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-2xl font-semibold transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 text-sm"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {saving ? 'Saving…' : submitLabel}
            </button>
        </div>
    );
}

function AddVesselModal({ show, onClose, onCreated }) {
    const [form, setForm] = useState(INITIAL_NEW_VESSEL);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!form.name.trim() || !form.imo_number.trim()) {
            setError('Vessel name and IMO number are required');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                imo_number: form.imo_number.trim(),
                type: form.type || 'cargo',
                tonnage: form.tonnage ? parseFloat(form.tonnage) : null,
                build_year: form.build_year || null,
                flag_country: form.flag_country || null,
                owner_name: form.owner_name || null,
            };
            const { data } = await api.post('/vessels', payload);

            const created = data?.id
                ? data
                : { id: null, name: payload.name, imo_number: payload.imo_number };

            if (created.id) {
                setSuccess(`Vessel "${created.name}" created and selected`);
                setTimeout(() => {
                    onCreated(created);
                    setForm(INITIAL_NEW_VESSEL);
                    setSuccess('');
                }, 1200);
            } else {
                setSuccess('Vessel created. Please select it from the dropdown.');
                setTimeout(() => {
                    onClose();
                    setForm(INITIAL_NEW_VESSEL);
                    setSuccess('');
                }, 1500);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add vessel');
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell onClose={onClose} title="Add New Vessel" icon={Ship}>
            {error && <ModalAlert type="error">{error}</ModalAlert>}
            {success && <ModalAlert type="success">{success}</ModalAlert>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModalInput label="Vessel Name *" name="name" value={form.name} onChange={handleChange} placeholder="e.g., MV Ever Given" />
                    <ModalInput label="IMO Number *" name="imo_number" value={form.imo_number} onChange={handleChange} placeholder="e.g., 9811000" mono />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">
                            Type
                        </label>
                        <select
                            name="type"
                            value={form.type}
                            onChange={handleChange}
                            className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all text-sm"
                        >
                            <option value="cargo">Cargo</option>
                            <option value="tanker">Tanker</option>
                            <option value="passenger">Passenger</option>
                            <option value="yacht">Yacht</option>
                            <option value="fishing">Fishing</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <ModalInput label="Flag Country" name="flag_country" value={form.flag_country} onChange={handleChange} placeholder="e.g., Panama" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModalInput label="Build Year" name="build_year" value={form.build_year} onChange={handleChange} placeholder="e.g., 2018" type="number" />
                    <ModalInput label="Tonnage (Gross)" name="tonnage" value={form.tonnage} onChange={handleChange} placeholder="e.g., 200000" type="number" />
                </div>
                <ModalInput label="Owner Name" name="owner_name" value={form.owner_name} onChange={handleChange} placeholder="Vessel owner name" />

                <ModalFooter onClose={onClose} saving={saving} submitLabel="Add Vessel" />
            </form>
        </ModalShell>
    );
}

function AddClientModal({ show, onClose, onCreated }) {
    const [form, setForm] = useState(INITIAL_NEW_CLIENT);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    if (!show) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!form.full_name.trim() || !form.email.trim()) {
            setError('Client name and email are required');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
            setError('Please enter a valid email address');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim(),
                phone: form.phone || null,
                company_name: form.company_name || null,
                kra_pin: form.kra_pin || null,
                pin: form.pin || null,
                role: 'direct_client',
                password: form.password || 'Temp123!@#',
            };
            const { data } = await api.post('/auth/register', payload);

            const created = data?.id
                ? { ...data, full_name: data.full_name || payload.full_name, email: data.email || payload.email }
                : null;

            if (created?.id) {
                setSuccess(`Client "${created.full_name}" created and selected`);
                setTimeout(() => {
                    onCreated(created);
                    setForm(INITIAL_NEW_CLIENT);
                    setSuccess('');
                }, 1200);
            } else {
                setSuccess(`Client "${payload.full_name}" created. Please search and select them.`);
                setTimeout(() => {
                    onClose();
                    setForm(INITIAL_NEW_CLIENT);
                    setSuccess('');
                }, 1800);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add client');
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell onClose={onClose} title="Add New Client" icon={UserPlus}>
            {error && <ModalAlert type="error">{error}</ModalAlert>}
            {success && <ModalAlert type="success">{success}</ModalAlert>}

            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-2xl text-xs text-blue-700 dark:text-blue-300 mb-4">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                This creates a new user with role <span className="font-mono font-semibold">direct_client</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModalInput label="Full Name *" name="full_name" value={form.full_name} onChange={handleChange} placeholder="e.g., John Doe" />
                    <ModalInput label="Email *" name="email" value={form.email} onChange={handleChange} placeholder="e.g., client@example.com" type="email" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModalInput label="Phone" name="phone" value={form.phone} onChange={handleChange} placeholder="e.g., +254 712 345 678" />
                    <ModalInput label="Company Name" name="company_name" value={form.company_name} onChange={handleChange} placeholder="e.g., ABC Trading Ltd" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ModalInput label="KRA PIN" name="kra_pin" value={form.kra_pin} onChange={handleChange} placeholder="e.g., P000600883U" mono />
                    <ModalInput label="PIN (Portal)" name="pin" value={form.pin} onChange={handleChange} placeholder="e.g., 123456" />
                </div>
                <div>
                    <ModalInput label="Default Password" name="password" value={form.password} onChange={handleChange} placeholder="Default password for client login" />
                    <p className="text-xs text-gray-400 mt-1.5">Client should change this password on first login</p>
                </div>

                <ModalFooter onClose={onClose} saving={saving} submitLabel="Create Client" />
            </form>
        </ModalShell>
    );
}

function AddBankModal({ existing, onClose, onAdd }) {
    const [value, setValue] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        const trimmed = value.trim();
        if (!trimmed) {
            setError('Enter a bank name');
            return;
        }
        if (existing.some((o) => o.value.toLowerCase() === trimmed.toLowerCase())) {
            setError('This bank already exists in the list');
            return;
        }
        onAdd(trimmed);
    };

    return (
        <ModalShell onClose={onClose} title="Add Bank" icon={Landmark} maxWidth="max-w-md">
            {error && <ModalAlert type="error">{error}</ModalAlert>}

            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-2xl text-xs text-blue-700 dark:text-blue-300 mb-4">
                <Info className="w-4 h-4 flex-shrink-0" />
                Custom banks are saved in this browser and appear in the dropdown next time.
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">
                        Bank Name *
                    </label>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="e.g., Gulf African Bank"
                        autoFocus
                        className="w-full border border-gray-200 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white rounded-2xl px-4 py-3 focus:ring-4 focus:ring-emerald-500/15 focus:border-[#3a663f] outline-none transition-all text-sm"
                    />
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 px-4 border border-gray-200 dark:border-slate-700 rounded-2xl text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-2xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 text-sm"
                    >
                        <Plus className="w-4 h-4" />
                        Add Bank
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

const ReviewModal = memo(function ReviewModal({ open, onClose, onConfirm, submitting, form, vessel, client, openCover, premium }) {
    if (!open) return null;

    const fmt = (v) =>
        new Intl.NumberFormat(undefined, { minimumFractionDigits: 2 }).format(v || 0);

    return (
        <ModalShell onClose={onClose} title="Review & Confirm" icon={CheckCircle}>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-5 leading-relaxed">
                Please review the details below before generating your quote.
            </p>

            <div className="bg-gray-50/80 dark:bg-slate-800/40 rounded-2xl p-5 space-y-3 mb-5 border border-gray-100 dark:border-slate-800">
                <ReviewRow label="Vessel" value={vessel?.name || '—'} />
                <ReviewRow label="Product" value={form.product_id ? `#${form.product_id}` : '—'} />
                <ReviewRow
                    label="Route"
                    value={
                        form.from_location && form.to_location
                            ? `${form.from_location} → ${form.to_location}`
                            : '—'
                    }
                />
                <ReviewRow label="Mode" value={form.mode_of_transport} />
                <ReviewRow
                    label="Sum Insured"
                    value={
                        form.sum_insured
                            ? `${form.currency === 'KES' ? 'KSh' : '$'} ${Number(form.sum_insured).toLocaleString()}`
                            : '—'
                    }
                />
                {form.custom_bank_interest && (
                    <ReviewRow label="Bank" value={form.custom_bank_interest} />
                )}
                <ReviewRow
                    label="Coverage Period"
                    value={
                        form.coverage_start && form.coverage_end
                            ? `${form.coverage_start} → ${form.coverage_end}`
                            : '—'
                    }
                />
                {client && <ReviewRow label="Client" value={client.full_name} />}
                {openCover && (
                    <ReviewRow
                        label="Open Cover"
                        value={`${openCover.cover_number} (Auto-approval)`}
                        highlight
                    />
                )}

                {premium && (
                    <div className="pt-4 border-t border-gray-200 dark:border-slate-700 space-y-2">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
                            <span>Basic Premium (Marine + War)</span>
                            <span className="tabular-nums">
                                {form.currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.grossPremium)}
                            </span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
                            <span>MCI Fee (15% of Basic Premium)</span>
                            <span className="tabular-nums">
                                {form.currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.mciFee)}
                            </span>
                        </div>
                        <div className="flex justify-between items-baseline pt-3 border-t border-gray-200 dark:border-slate-700">
                            <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">
                                Total Premium
                            </span>
                            <span className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                {form.currency === 'KES' ? 'KSh ' : '$'}{fmt(premium.totalPremium)}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={submitting}
                    className="flex-1 py-3 px-4 border border-gray-200 dark:border-slate-700 rounded-2xl text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-semibold text-sm disabled:opacity-50"
                >
                    Go Back
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={submitting}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-2xl font-bold transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/20 text-sm"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating…
                        </>
                    ) : (
                        <>
                            <CheckCircle className="w-4 h-4" />
                            Confirm & Generate
                        </>
                    )}
                </button>
            </div>
        </ModalShell>
    );
});

function ReviewRow({ label, value, highlight }) {
    return (
        <div className="flex justify-between items-start gap-2">
            <span className="text-sm text-gray-500 dark:text-slate-400 flex-shrink-0">{label}</span>
            <span className={`text-sm text-right ${highlight ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'font-medium text-gray-800 dark:text-white'}`}>
                {value}
            </span>
        </div>
    );
}